import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Card, Button, Container } from "react-bootstrap";
import Swal from "sweetalert2";
import PaginatedTable from "../Common/PaginatedTable";
import EntityDetailsSidebar from "../Common/EntityDetailsSidebar";
import { formatDateTime } from "../../utils/formatters";
import {
  getCompanyLicenses,
  deleteCompanyLicense,
} from "../../services/companyLicenseService";

import { Pencil, Trash2, Eye, BadgePercent, CalendarClock } from "lucide-react";
import dayjs from "dayjs";
import isBetween from "dayjs/plugin/isBetween";
dayjs.extend(isBetween);

const CompanyLicensePage = () => {
  const navigate = useNavigate();

  // Estados
  const [licenses, setLicenses] = useState([]);
  const [selectedLicense, setSelectedLicense] = useState(null);
  const [totalRows, setTotalRows] = useState(0);
  const [perPage, setPerPage] = useState(5);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");

  // Esquema del sidebar
  const schema = [
    { label: "Tipo de licencia", key: "licenseType" },
    { label: "Máximo de usuarios", key: "maxUsers" },
    {
      label: "Usuarios en uso",
      key: "usedUsers",
      render: (value) => (!value ? "Sin usuarios" : value),
    },
    {
      label: "Inicio",
      key: "startDate",
      render: (value) => (value ? formatDateTime(value) : "No aplica"),
    },
    {
      label: "Fin",
      key: "endDate",
      render: (value) => (value ? formatDateTime(value) : "No aplica"),
    },
    {
      label: "Creado",
      key: "createdAt",
      render: formatDateTime,
    },
    {
      label: "Última modificación",
      key: "updatedAt",
      render: formatDateTime,
    },
    {
      label: "Notas",
      key: "notes",
      render: (value) => (
        <div
          style={{
            whiteSpace: "pre-wrap",
            fontStyle: "italic",
            color: "#6c757d",
          }}
        >
          {value || "—"}
        </div>
      ),
    },
  ];

  /**
   * Consulta paginada al backend considerando filtro de búsqueda.
   */
  const fetchLicenses = useCallback(async (page, size, term = "") => {
    try {
      const response = await getCompanyLicenses(page, size, term);
      if (response.success) {
        setLicenses(response.data.items);
        setTotalRows(response.data.totalCount);
      }
    } catch (err) {
      Swal.fire("Error", "No se pudieron cargar las licencias", "error");
    }
  }, []);

  useEffect(() => {
    fetchLicenses(currentPage, perPage, searchTerm);
  }, [fetchLicenses, currentPage, perPage, searchTerm]);

  const handlePageChange = (page) => setCurrentPage(page);
  const handlePerRowsChange = (newPerPage, page) => {
    setPerPage(newPerPage);
    setCurrentPage(page);
  };
  const handleSearch = (term) => {
    setSearchTerm(term);
    setCurrentPage(1);
  };

  const handleEdit = (id) => navigate(`/dashboard/company-license/edit/${id}`);

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: "¿Estás seguro?",
      text: "Esta acción eliminará la licencia.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sí, eliminar",
      cancelButtonText: "Cancelar",
    });

    if (result.isConfirmed) {
      try {
        const response = await deleteCompanyLicense(id);
        if (response.success) {
          Swal.fire("Eliminado", "La licencia ha sido eliminada.", "success");
          fetchLicenses(currentPage, perPage, searchTerm);
        } else {
          throw new Error(response.message);
        }
      } catch (err) {
        Swal.fire("Error", "No se pudo eliminar la licencia", "error");
      }
    }
  };

  const columns = [
    {
      name: "Empresa",
      selector: (row) => row.companyName,
      wrap: true,
    },
    {
      name: "Usuarios",
      selector: (row) =>
        row.isUnlimited ? "Ilimitado" : `${row.usedUsers}/${row.maxUsers}`,
      center: true,
      wrap: true,
      width: "200px",
    },
    {
      name: "Fechas",
      selector: (row) =>
        row.startDate && row.endDate
          ? `${formatDateTime(row.startDate)} - ${formatDateTime(row.endDate)}`
          : "N/A",
      wrap: true,
      width: "350px",
    },
    {
      name: "Acciones",
      cell: (row) => (
        <div className="d-flex gap-2">
          <Button
            variant="outline-primary"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              handleEdit(row.companyLicenseId);
            }}
            title="Editar"
          >
            <Pencil size={16} />
          </Button>
          <Button
            variant="outline-danger"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              handleDelete(row.companyLicenseId);
            }}
            title="Eliminar"
          >
            <Trash2 size={16} />
          </Button>
          <Button
            variant="outline-info"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              setSelectedLicense(row);
            }}
            title="Ver detalles"
          >
            <Eye size={16} />
          </Button>
        </div>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
      wrap: true,
      width: "150px",
      center: true,
    },
  ];

  return (
    <div className="container-fluid mt-4 px-4 w-100">
      <Card className="p-4">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h4 className="mb-0 text-dark">Licencias de empresa</h4>
          <Button onClick={() => navigate("/dashboard/company-license/create")}>
            + Crear nueva licencia
          </Button>
        </div>

        <PaginatedTable
          keyField="companyLicenseId"
          columns={columns}
          data={licenses}
          totalRows={totalRows}
          perPage={perPage}
          currentPage={currentPage}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handlePerRowsChange}
          onSearch={handleSearch}
          searchKeys={["companyName"]}
        />
      </Card>

      <EntityDetailsSidebar
        isVisible={!!selectedLicense}
        onClose={() => setSelectedLicense(null)}
        title="Detalles de la licencia"
        icon={
          selectedLicense?.isUnlimited ? (
            <BadgePercent size={20} />
          ) : (
            <CalendarClock size={20} />
          )
        }
        data={{
          ...selectedLicense,
          fullName: selectedLicense?.companyName,
          email: "",
          isActive: (() => {
            if (selectedLicense?.isUnlimited) return true;

            const now = dayjs();
            const start = selectedLicense?.startDate
              ? dayjs(selectedLicense.startDate)
              : null;
            const end = selectedLicense?.endDate
              ? dayjs(selectedLicense.endDate)
              : null;

            if (!start && !end) return true;
            if (start && !end)
              return now.isAfter(start) || now.isSame(start, "day");
            if (!start && end)
              return now.isBefore(end) || now.isSame(end, "day");
            if (start && end) return now.isBetween(start, end, null, "[]");

            return false;
          })(),
          licenseType: selectedLicense?.isUnlimited
            ? "Ilimitada"
            : "Por fechas",
        }}
        schema={schema}
      />
    </div>
  );
};

export default CompanyLicensePage;
